import React, { useState, useEffect, useCallback } from 'react';
import * as adminService from '../../services/admin.service';

/**
 * Tech Spec — Jenn, 2026-09-06: "the inside 365reset version" is supposed
 * to live here, in the back engine, and be the same content as
 * 365reset-tech-spec.md in the shared Google Drive folder (the one only
 * she and Claude have access to). Claude is the one who keeps both in
 * sync — this page is the in-app mirror, not a second independently-edited
 * copy, so it always shows exactly what the Drive doc says.
 */
export default function AdminTechSpec({ embedded }) {
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      setDoc(await adminService.getTechSpec());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the tech spec.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEditing = () => {
    setDraft(doc?.content || '');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      setDoc(await adminService.updateTechSpec(draft));
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the tech spec.');
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <>
      {!embedded && <h1 className="font-serif text-2xl font-bold text-reset-brown mb-1">Tech Spec</h1>}
      <p className="text-sm text-gray-500 mb-5">
        Mirrors <span className="font-mono">365reset-tech-spec.md</span> in the shared Google Drive folder — Claude keeps both updated together.
        {doc?.updated_at && (
          <> Last updated {new Date(doc.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}.</>
        )}
      </p>

      {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

      {!doc ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : editing ? (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={30}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 text-sm text-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex justify-end mb-3">
            <button
              onClick={startEditing}
              className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg text-reset-brown hover:bg-gray-50"
            >
              Edit
            </button>
          </div>
          {doc.content ? (
            <pre className="whitespace-pre-wrap text-xs font-mono text-reset-brown leading-relaxed">{doc.content}</pre>
          ) : (
            <p className="text-sm text-gray-400">Nothing here yet.</p>
          )}
        </div>
      )}
    </>
  );

  return body;
}
